import { ApplicationController } from '@aha-app/mvc';
import ExtensionContribution from 'components/framework/models/ExtensionContribution';
import FredController from './FredController';
import CommandStart from '../outputs/CommandStart';
import CommandError from '../outputs/CommandError';
import CommandSuccess from '../outputs/CommandSuccess';
import CommandPrompt from '../outputs/CommandPrompt';
import CommandMessage from '../outputs/CommandMessage';

class CommandController extends ApplicationController {
  static PHASE_RUNNING = 0;
  static PHASE_DONE = 1;
  static PHASE_PROMPTING = 2;

  static initialState = {
    // Command that started the current run. Displayed at top of modal.
    firstCommand: null,
    // Current phase of the state machine.
    phase: CommandController.PHASE_RUNNING,
    // Stack of executing commands. The current command is on the top of the
    // stack. The stack gets deeper if one command calls another.
    commands: [],
    // Array of CommandOutput objects displayed to the user during the command
    // execution.
    outputs: [],
  };

  async initialize() {
    window.commandController = this;
  }

  destroy() {
    window.commandController = null;
  }

  async actionExecuteCommandByIdentifier(identifier, commandArgs, callback) {
    const command = await ExtensionContribution.select(
      'id',
      'name',
      'identifier'
    )
      .where({ identifier })
      .all(); // TODO: Seems wrong to need to call `all` here. `first()` should execute the query.
    const result = await this.actionExecuteCommand(command[0], commandArgs);
    if (callback) callback(result);
  }

  async actionExecuteCommand(command, commandArgs) {
    if (!(command instanceof ExtensionContribution)) {
      throw new Error(
        `Attempting to execute a command by identifier: '${command}'`
      );
    }

    const { ahaCommand } = commandArgs;

    this.parent.actionSelectTab(FredController.TAB_COMMAND_RUNNER);
    this.actionAddOutput(new CommandStart(command, ahaCommand));
    if (this.state.commands.length === 0) {
      this.state.firstCommand = command;

      if (ahaCommand) {
        this.state.ahaCommand = ahaCommand;
      }
    }
    this.state.commands.push(command);

    this.state.phase = CommandController.PHASE_RUNNING;
    const result = await this.internalExecuteCommand(command, commandArgs);
    this.state.phase = CommandController.PHASE_DONE;
    if (result.error) {
      this.actionAddOutput(new CommandError(result.error, result.errorDetails));
    } else {
      this.actionAddOutput(new CommandSuccess(command));
    }
    this.state.commands.pop();

    // Close the modal if there is nothing interesting in the output.
    if (!this.interestingOutputs() && ahaCommand?.closeAfterCommand !== false) {
      this.parent.actionClose();
    }

    return result.result;
  }

  actionAddOutput(output) {
    output.depth = this.state.commands.length;
    this.state.outputs.push(output);
  }

  actionAddMessage(message) {
    this.actionAddOutput(new CommandMessage(message));
  }

  async internalExecuteCommand(command, commandArgs) {
    let result = null;
    let error = null;
    let errorDetails = null;
    try {
      result = await window.aha.executeCommand(command.identifier, commandArgs);
    } catch (e) {
      console.error(`Command '${command.identifier}' had unhandled error:`);
      console.log(e.stack);
      error = e.message;
      errorDetails = e.stack;
    }
    return { result, error, errorDetails };
  }

  async actionPrompt(prompt, options) {
    this.state.phase = CommandController.PHASE_PROMPTING;

    const promptOutput = new CommandPrompt(this, prompt, options);
    this.actionAddOutput(promptOutput);

    return promptOutput.waitForAnswer();
  }

  actionPromptComplete() {
    this.state.phase = CommandController.PHASE_RUNNING;
  }

  // See if there are any interesting outputs to display.
  interestingOutputs() {
    return this.state.outputs.find(
      element =>
        // Check for any outputs that user needs to read.
        element instanceof CommandMessage || element instanceof CommandError
    );
  }
}

export default CommandController;
